/**
 * EnvSecretsProvider — read-only provider that resolves secrets from environment variables.
 *
 * Refs are expected to be plain env var names (e.g. `DATABASE_URL`). The provider is
 * always available with zero configuration and requires no encryption or keychain access.
 * `setSecret` and `deleteSecret` always throw `SecretAccessDeniedError` because
 * environment variables are managed externally to the running process.
 *
 * @docLink packages/secrets/concepts#env-secrets-provider
 */

import type {
  ISecretsProvider,
  ListFilter,
  ProviderCapabilities,
  SecretMetadata,
  SetSecretOptions,
} from "@skaile/workspaces/plugins";
import { SecretAccessDeniedError, SecretNotFoundError } from "@skaile/workspaces/plugins";

/**
 * Sentinel timestamp for env secrets, which have no real created/updated time.
 * The Unix epoch is unambiguously not "now", so consumers can recognise it as
 * "unknown" rather than treating it as a genuine recent timestamp.
 */
const ENV_UNKNOWN_TIMESTAMP = new Date(0);

export class EnvSecretsProvider implements ISecretsProvider {
  readonly id = "env";

  async getSecret(ref: string): Promise<Uint8Array> {
    const value = process.env[ref];
    if (value === undefined) {
      throw new SecretNotFoundError(ref);
    }
    return new TextEncoder().encode(value);
  }

  async setSecret(_ref: string, _value: Uint8Array, _opts?: SetSecretOptions): Promise<void> {
    throw new SecretAccessDeniedError("EnvSecretsProvider is read-only");
  }

  async deleteSecret(_ref: string): Promise<void> {
    throw new SecretAccessDeniedError("EnvSecretsProvider is read-only");
  }

  async listSecrets(filter?: ListFilter): Promise<string[]> {
    let keys = Object.keys(process.env);
    if (filter?.prefix) {
      keys = keys.filter((k) => k.startsWith(filter.prefix!));
    }
    if (filter?.limit) {
      keys = keys.slice(0, filter.limit);
    }
    return keys;
  }

  async testConnection(): Promise<{ ok: boolean; latencyMs?: number; error?: string }> {
    return { ok: true, latencyMs: 0 };
  }

  async getMetadata(ref: string): Promise<SecretMetadata> {
    const value = process.env[ref];
    if (value === undefined) {
      throw new SecretNotFoundError(ref);
    }
    // Env vars carry no creation/modification time. `SecretMetadata` requires
    // both fields, so use the Unix epoch as a documented "unknown" sentinel
    // rather than `new Date()`, which would falsely imply the secret was just
    // created/updated. The read-only scope summary already signals the source.
    return {
      ref,
      createdAt: ENV_UNKNOWN_TIMESTAMP,
      updatedAt: ENV_UNKNOWN_TIMESTAMP,
      scopeSummary: "environment variable (read-only)",
    };
  }

  getCapabilities(): ProviderCapabilities {
    return {
      supportsRotation: false,
      supportsExpiry: false,
      supportsBatch: false,
      scopeModel: "flat",
    };
  }
}
